<div class="page school-settings-page">
  <div class="page-header">
    <div>
      <h1>{{ 'SCHOOL.TITLE' | translate }}</h1>
      <p class="page-subtitle">{{ 'SCHOOL.SUBTITLE' | translate }}</p>
    </div>
    <div class="page-actions">
      <button
        type="button"
        class="btn btn-primary"
        *appAccess="'school:edit'"
        (click)="save()"
        [disabled]="loading || saving"
      >
        <mat-icon>{{ saving ? 'hourglass_empty' : 'save' }}</mat-icon>
        {{ (saving ? 'COMMON.SAVING' : 'SCHOOL.SAVE') | translate }}
      </button>
    </div>
  </div>

  <div class="settings-body" *ngIf="!loading; else loadingState">
    <div class="alert alert-error" *ngIf="isError">{{ errorMessage | translate }}</div>
    <div class="alert alert-success" *ngIf="successMessage">{{ successMessage | translate }}</div>

    <form [formGroup]="form" (ngSubmit)="save()" class="settings-form">
      <section class="settings-panel">
        <div class="panel-header">
          <div class="panel-icon" aria-hidden="true">
            <mat-icon>apartment</mat-icon>
          </div>
          <div class="panel-header-text">
            <h2>{{ 'SCHOOL.IDENTITY' | translate }}</h2>
            <p>{{ 'SCHOOL.IDENTITY_HINT' | translate }}</p>
          </div>
        </div>

        <div class="identity-grid">
          <div class="form-field">
            <label for="schoolName">{{ 'SCHOOL.SCHOOL_NAME' | translate }}</label>
            <input
              id="schoolName"
              type="text"
              formControlName="schoolName"
              [placeholder]="'SCHOOL.SCHOOL_NAME_PLACEHOLDER' | translate"
            />
            <span class="error" *ngIf="form.get('schoolName')?.touched && form.get('schoolName')?.hasError('required')">
              {{ 'SCHOOL.SCHOOL_NAME_REQUIRED' | translate }}
            </span>
          </div>

          <div class="form-field">
            <label for="departmentName">{{ 'SCHOOL.DEPARTMENT' | translate }}</label>
            <input
              id="departmentName"
              type="text"
              formControlName="departmentName"
              [placeholder]="'SCHOOL.DEPARTMENT_PLACEHOLDER' | translate"
            />
          </div>

          <div class="form-field">
            <label for="headName">{{ 'SCHOOL.PRINCIPAL' | translate }}</label>
            <input
              id="headName"
              type="text"
              formControlName="headName"
              [placeholder]="'SCHOOL.PRINCIPAL_PLACEHOLDER' | translate"
            />
          </div>
        </div>
      </section>

      <section class="settings-panel">
        <div class="panel-header">
          <div class="panel-icon" aria-hidden="true">
            <mat-icon>badge</mat-icon>
          </div>
          <div class="panel-header-text">
            <h2>{{ 'SCHOOL.STUDENT_CARDS' | translate }}</h2>
            <p>{{ 'SCHOOL.STUDENT_CARDS_HINT' | translate }}</p>
          </div>
        </div>

        <div class="card-colors-grid" formGroupName="studentCardColors">
          <div class="form-field color-field">
            <label for="busCardBackground">{{ 'SCHOOL.BUS_CARD_COLOR' | translate }}</label>
            <div class="color-input-row">
              <input
                id="busCardBackground"
                type="color"
                formControlName="busBackground"
                [attr.aria-label]="'SCHOOL.BUS_CARD_COLOR' | translate"
              />
              <span class="color-swatch-label">{{ form.get('studentCardColors.busBackground')?.value }}</span>
            </div>
          </div>

          <div class="form-field color-field">
            <label for="noBusCardBackground">{{ 'SCHOOL.NO_BUS_CARD_COLOR' | translate }}</label>
            <div class="color-input-row">
              <input
                id="noBusCardBackground"
                type="color"
                formControlName="noBusBackground"
                [attr.aria-label]="'SCHOOL.NO_BUS_CARD_COLOR' | translate"
              />
              <span class="color-swatch-label">{{ form.get('studentCardColors.noBusBackground')?.value }}</span>
            </div>
          </div>

          <div class="form-field">
            <label for="cutMarginMm">{{ 'SCHOOL.CUT_MARGIN_MM' | translate }}</label>
            <input
              id="cutMarginMm"
              type="number"
              formControlName="cutMarginMm"
              min="0"
              max="20"
              step="0.5"
              [placeholder]="'SCHOOL.CUT_MARGIN_MM_PLACEHOLDER' | translate"
            />
            <span class="field-hint">{{ 'SCHOOL.CUT_MARGIN_MM_HINT' | translate }}</span>
          </div>
        </div>
      </section>

      <section class="settings-panel">
        <div class="panel-header">
          <div class="panel-icon" aria-hidden="true">
            <mat-icon>groups</mat-icon>
          </div>
          <div class="panel-header-text">
            <h2>{{ 'SCHOOL.HEADS' | translate }}</h2>
            <p>{{ 'SCHOOL.HEADS_HINT' | translate }}</p>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" (click)="addSectionHead()">
            <mat-icon>add</mat-icon>
            {{ 'SCHOOL.ADD_HEAD' | translate }}
          </button>
        </div>

        <div class="heads-empty" *ngIf="!sectionHeads.length">
          <mat-icon>person_add</mat-icon>
          <strong>{{ 'SCHOOL.NO_HEADS' | translate }}</strong>
          <span>{{ 'SCHOOL.NO_HEADS_HINT' | translate }}</span>
          <button type="button" class="btn btn-primary btn-sm" *appAccess="'school:edit'" (click)="addSectionHead()">
            <mat-icon>add</mat-icon>
            {{ 'SCHOOL.ADD_FIRST_HEAD' | translate }}
          </button>
        </div>

        <ul class="heads-list" *ngIf="sectionHeads.length">
          <li
            class="head-row"
            *ngFor="let group of sectionHeads.controls; let index = index"
            [formGroup]="group"
          >
            <span class="head-index">{{ index + 1 }}</span>
            <div class="form-field">
              <label [for]="'sectionHead' + index">{{ 'SCHOOL.HEAD_NAME' | translate }}</label>
              <input
                [id]="'sectionHead' + index"
                type="text"
                formControlName="name"
                [placeholder]="'SCHOOL.HEAD_NAME_PLACEHOLDER' | translate"
              />
              <span class="error" *ngIf="group.get('name')?.touched && group.get('name')?.hasError('required')">
                {{ 'COMMON.NAME_REQUIRED' | translate }}
              </span>
            </div>
            <button
              type="button"
              class="btn btn-ghost btn-icon"
              *appAccess="'school:edit'"
              (click)="removeSectionHead(index)"
              [attr.aria-label]="'SCHOOL.REMOVE_HEAD' | translate"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </li>
        </ul>
      </section>

      <section class="settings-panel" formGroupName="attendanceSchedule">
        <div class="panel-header">
          <div class="panel-icon" aria-hidden="true">
            <mat-icon>schedule</mat-icon>
          </div>
          <div class="panel-header-text">
            <h2>{{ 'SCHOOL.ATTENDANCE' | translate }}</h2>
            <p>{{ 'SCHOOL.ATTENDANCE_HINT' | translate }}</p>
          </div>
        </div>

        <h3 class="panel-subtitle">{{ 'SCHOOL.DEFAULT_TIMES' | translate }}</h3>
        <p class="panel-note">{{ 'SCHOOL.DEFAULT_TIMES_HINT' | translate }}</p>

        <label class="late-toggle">
          <input
            type="checkbox"
            formControlName="lateEnabled"
            (change)="onLateEnabledChange()"
          />
          <span>
            <strong>{{ 'SCHOOL.LATE_ENABLED' | translate }}</strong>
            <small>{{ 'SCHOOL.LATE_ENABLED_HINT' | translate }}</small>
          </span>
        </label>

        <div class="identity-grid">
          <div class="form-field">
            <label for="checkInOpen">{{ 'SCHOOL.CHECK_IN_OPEN' | translate }}</label>
            <input id="checkInOpen" type="time" formControlName="checkInOpen" />
          </div>
          <div class="form-field" *ngIf="lateEnabled">
            <label for="checkInLateAfter">{{ 'SCHOOL.CHECK_IN_LATE_AFTER' | translate }}</label>
            <input id="checkInLateAfter" type="time" formControlName="checkInLateAfter" />
          </div>
          <div class="form-field">
            <label for="checkOutOpen">{{ 'SCHOOL.CHECK_OUT_OPEN' | translate }}</label>
            <input id="checkOutOpen" type="time" formControlName="checkOutOpen" />
          </div>
          <div class="form-field" *ngIf="lateEnabled">
            <label for="checkOutLateAfter">{{ 'SCHOOL.CHECK_OUT_LATE_AFTER' | translate }}</label>
            <input id="checkOutLateAfter" type="time" formControlName="checkOutLateAfter" />
          </div>
        </div>

        <div class="weekday-header">
          <div>
            <h3 class="panel-subtitle">{{ 'SCHOOL.WEEKDAY_SCHEDULE' | translate }}</h3>
            <p class="panel-note">{{ 'SCHOOL.WEEKDAY_SCHEDULE_HINT' | translate }}</p>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" (click)="applyDefaultsToSchoolDays()">
            {{ 'SCHOOL.APPLY_DEFAULTS' | translate }}
          </button>
        </div>

        <div class="weekday-table-wrap">
          <table class="weekday-table">
            <thead>
              <tr>
                <th>{{ 'SCHOOL.WEEKDAY' | translate }}</th>
                <th>{{ 'SCHOOL.HOLIDAY' | translate }}</th>
                <th>{{ 'SCHOOL.CHECK_IN_OPEN' | translate }}</th>
                <th *ngIf="lateEnabled">{{ 'SCHOOL.CHECK_IN_LATE_AFTER' | translate }}</th>
                <th>{{ 'SCHOOL.CHECK_OUT_OPEN' | translate }}</th>
                <th *ngIf="lateEnabled">{{ 'SCHOOL.CHECK_OUT_LATE_AFTER' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let weekday of weekdays" [class.is-holiday]="isHoliday(weekday)">
                <td class="weekday-name">{{ ('SCHOOL.WEEKDAY_' + weekday) | translate }}</td>
                <td>
                  <label class="holiday-check">
                    <input
                      type="checkbox"
                      [checked]="isHoliday(weekday)"
                      (change)="toggleHoliday(weekday, $any($event.target).checked)"
                    />
                    <span>{{ 'SCHOOL.HOLIDAY' | translate }}</span>
                  </label>
                </td>
                <ng-container [formGroup]="dayTimesGroup(weekday)">
                  <td>
                    <input type="time" formControlName="checkInOpen" />
                  </td>
                  <td *ngIf="lateEnabled">
                    <input type="time" formControlName="checkInLateAfter" />
                  </td>
                  <td>
                    <input type="time" formControlName="checkOutOpen" />
                  </td>
                  <td *ngIf="lateEnabled">
                    <input type="time" formControlName="checkOutLateAfter" />
                  </td>
                </ng-container>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="panel-note">{{ 'SCHOOL.HOLIDAY_HINT' | translate }}</p>

        <div class="weekday-header">
          <div>
            <h3 class="panel-subtitle">{{ 'SCHOOL.HOLIDAY_DATES' | translate }}</h3>
            <p class="panel-note">{{ 'SCHOOL.HOLIDAY_DATES_HINT' | translate }}</p>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" *appAccess="'school:edit'" (click)="addHolidayDate()">
            <mat-icon>add</mat-icon>
            {{ 'SCHOOL.ADD_HOLIDAY_DATE' | translate }}
          </button>
        </div>

        <div class="heads-empty" *ngIf="!holidayDates.length">
          <mat-icon>event</mat-icon>
          <strong>{{ 'SCHOOL.NO_HOLIDAY_DATES' | translate }}</strong>
          <span>{{ 'SCHOOL.NO_HOLIDAY_DATES_HINT' | translate }}</span>
          <button type="button" class="btn btn-primary btn-sm" *appAccess="'school:edit'" (click)="addHolidayDate()">
            <mat-icon>add</mat-icon>
            {{ 'SCHOOL.ADD_HOLIDAY_DATE' | translate }}
          </button>
        </div>

        <ul class="heads-list holiday-dates-list" *ngIf="holidayDates.length">
          <li
            class="head-row holiday-date-row"
            *ngFor="let group of holidayDates.controls; let index = index"
            [formGroup]="group"
          >
            <span class="head-index">{{ index + 1 }}</span>
            <div class="form-field">
              <label [for]="'holidayName' + index">{{ 'SCHOOL.HOLIDAY_NAME' | translate }}</label>
              <input
                [id]="'holidayName' + index"
                type="text"
                formControlName="name"
                [placeholder]="'SCHOOL.HOLIDAY_NAME_PLACEHOLDER' | translate"
              />
              <span class="error" *ngIf="group.get('name')?.touched && group.get('name')?.hasError('required')">
                {{ 'COMMON.NAME_REQUIRED' | translate }}
              </span>
            </div>
            <div class="form-field">
              <label [for]="'holidayDate' + index">{{ 'SCHOOL.HOLIDAY_DATE' | translate }}</label>
              <input [id]="'holidayDate' + index" type="date" formControlName="date" />
              <span class="error" *ngIf="group.get('date')?.touched && group.get('date')?.hasError('required')">
                {{ 'COMMON.REQUIRED_FIELDS' | translate }}
              </span>
            </div>
            <button
              type="button"
              class="btn btn-ghost btn-icon"
              *appAccess="'school:edit'"
              (click)="removeHolidayDate(index)"
              [attr.aria-label]="'SCHOOL.REMOVE_HOLIDAY_DATE' | translate"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </li>
        </ul>
      </section>
    </form>
  </div>

  <ng-template #loadingState>
    <div class="settings-loading">
      <span class="spinner" aria-hidden="true"></span>
      {{ 'SCHOOL.LOADING' | translate }}
    </div>
  </ng-template>
</div>
